import { Component, computed, inject, OnDestroy, signal } from '@angular/core';
import { FormsModule } from "@angular/forms";
import { RepoService } from "../services/repo.service";
import { ContactResponse, AuthUser, AppDetails } from "../models/dynamics-contact-response";
import { finalize, Subscription } from "rxjs";
import { DatePipe, NgTemplateOutlet } from "@angular/common";

@Component({
  selector: 'app-health-check',
  imports: [
    FormsModule,
    DatePipe,
    NgTemplateOutlet,
  ],
  templateUrl: './health-check.component.html',
  styleUrl: './health-check.component.css'
})
export class HealthCheckComponent implements OnDestroy {
  private sub: Subscription | undefined;

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  repo = inject(RepoService)

  email: string = "pb83+456@hotmail.com";
  isAnalyzing = signal(false)

  dynamicsChecked = signal(false)
  dynamicsValidation = computed(() => {
    if (!this.dynamicsChecked()) return 'default';
    return (this.virtualKeyValidation() && this.countryValidation() && this.statusValidation() && this.rolesValidation() && this.driverContractDriivzIdValidation() && this.subcontractDriivzIdValidation() && this.rfidDriivzIdValidation()) ? 'valid' : 'invalid';
  })
  virtualKeyValidation = signal(false)
  countryValidation = signal(false)
  statusValidation = signal(false)
  rolesValidation = signal(false)
  driverContractDriivzIdValidation = signal(false)
  subcontractDriivzIdValidation = signal(false)
  subcontractDriivzIdCounts = signal("0/0")
  rfidDriivzIdValidation = signal(false)
  rfidDriivzIdCounts = signal("0/0")

  azureValidation = signal<'default' | 'valid' | 'invalid'>('default')
  authUsers = signal<AuthUser[]>([])
  azureUserExists = signal(false)

  sendGridValidation = signal<'default' | 'valid' | 'invalid'>('default')
  sendGridEmailMarkedAsSpam = signal(false)

  appDetails = signal<AppDetails | null>(null)
  contactId = signal<string | null>(null)


  onAnalyzeClicked() {
    if (this.email == "" || this.isAnalyzing()) {
      return;
    }

    this.isAnalyzing.set(true);
    this.sub = this.repo.getDynamicsData(this.email)
      .pipe(finalize(() => this.isAnalyzing.set(false)))
      .subscribe({
        next: (res) => {
          this.checkDynamics(res);
          this.validateAzure(res.authUsers);
          this.validateSendGrid(res.sendGridSpamReports);
        },
        error: (err) => {
          console.error("Analysis failed", err);
        }
      });
  }

  private checkDynamics(contactResponse: ContactResponse) {

    this.contactId.set(contactResponse?.contactAggregate?.contact?.id ?? null);
    this.appDetails.set(contactResponse?.appDetails ?? null);

    this.virtualKeyValidation
      .set(contactResponse?.contactAggregate?.keys != null && contactResponse.contactAggregate.keys.length > 0)

    this.countryValidation
      .set(contactResponse?.address?.country != null && contactResponse.address.country != "")

    this.statusValidation.set(contactResponse?.contactAggregate?.contact?.status == "Active")

    const hasRequiredRole = contactResponse?.contactAggregate?.contact?.roles?.some(role =>
      role.roleKeys && role.roleKeys.length > 0
    ) ?? false;

    this.rolesValidation.set(hasRequiredRole)

    this.driverContractDriivzIdValidation
      .set(contactResponse?.contactAggregate?.driverContract?.driivzId != null && contactResponse.contactAggregate.driverContract?.driivzId != "")

    const subcontracts = contactResponse?.contactAggregate?.driverContract?.subContracts;
    if (subcontracts && subcontracts.length > 0) {
      const firstSubcontract = subcontracts[0];
      const driivzIds = firstSubcontract?.subContractDetails?.subcontractDriivzIds;
      if (driivzIds) {
        const total = driivzIds.length;
        const valid = driivzIds.filter(id => id != null && id !== "").length;
        this.subcontractDriivzIdCounts.set(`${valid}/${total}`);
        this.subcontractDriivzIdValidation.set(valid === total && total > 0);
      } else {
        this.subcontractDriivzIdCounts.set("0/0");
        this.subcontractDriivzIdValidation.set(false);
      }
    } else {
      this.subcontractDriivzIdCounts.set("0/0");
      this.subcontractDriivzIdValidation.set(false);
    }

    const keys = contactResponse?.contactAggregate?.keys;
    if (keys && keys.length > 0) {
      const total = keys.length;
      const valid = keys.filter(key => key.driivzId != null && key.driivzId !== "").length;
      this.rfidDriivzIdCounts.set(`${valid}/${total}`);
      this.rfidDriivzIdValidation.set(valid === total);
    } else {
      this.rfidDriivzIdCounts.set("0/0");
      this.rfidDriivzIdValidation.set(false);
    }

    this.dynamicsChecked.set(true)
  }

  private validateAzure(authUsers: AuthUser[]) {
    this.authUsers.set(authUsers);
    const exists = authUsers && authUsers.length > 0 && authUsers[0].id!=null;
    this.azureUserExists.set(exists)
    this.azureValidation.set(exists ? 'valid' : 'invalid')
  }


  private validateSendGrid(sendGridSpamReport: {}[]) {
    const isSpam = (sendGridSpamReport?.length ?? 0) > 0;
    this.sendGridEmailMarkedAsSpam.set(isSpam);
    this.sendGridValidation.set(isSpam ? 'invalid' : 'valid');
  }

  removeEmailFromSpam() {
    this.repo.removeEmailFromSendGridSpam(this.email).subscribe();
  }
}
